import React, { useState, useRef } from 'react'
import { colors } from '../../constants/colors'

const UploadMediaModal = ({ isOpen, onClose, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null)

  if (!isOpen) return null

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      setSelectedFile(files[0])
    }
  }

  const handleFileSelect = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setSelectedFile(files[0])
    }
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    try {
      await onUpload(selectedFile)
      setSelectedFile(null)
      onClose()
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    setSelectedFile(null)
    onClose()
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }

  const modalStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '12px',
    padding: '32px',
    width: '600px',
    maxWidth: '90vw',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const titleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 24px 0'
  }

  const uploadAreaStyle = {
    border: `2px dashed ${isDragging ? colors.primary : colors.borderVariants.medium}`,
    borderRadius: '8px',
    padding: '60px 40px',
    textAlign: 'center',
    backgroundColor: isDragging ? '#EFF6FF' : '#F8FAFC',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '24px'
  }

  const iconStyle = {
    width: '64px',
    height: '64px',
    margin: '0 auto 16px',
    color: colors.primary
  }

  const uploadTextStyle = {
    fontSize: '16px',
    color: colors.text.primary,
    marginBottom: '12px',
    fontWeight: '500'
  }

  const browseButtonStyle = {
    backgroundColor: '#EFF6FF',
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const fileInfoStyle = {
    padding: '16px',
    backgroundColor: colors.background.secondary,
    borderRadius: '8px',
    marginBottom: '24px'
  }

  const fileNameStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '4px'
  }

  const fileSizeStyle = {
    fontSize: '12px',
    color: colors.text.secondary
  }

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  }

  const cancelButtonStyle = {
    backgroundColor: 'white',
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const uploadButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: isUploading || !selectedFile ? 'not-allowed' : 'pointer',
    opacity: isUploading || !selectedFile ? 0.7 : 1,
    transition: 'background-color 0.2s ease'
  }

  return (
    <div style={modalOverlayStyle} onClick={handleCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>Upload Media</h2>

        <div
          style={uploadAreaStyle}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <svg style={iconStyle} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p style={uploadTextStyle}>Drag and Drop Files here or</p>
          <button
            type="button"
            style={browseButtonStyle}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#DBEAFE'
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#EFF6FF'
            }}
          >
            Browse Files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept="image/*,video/*,audio/*,application/pdf"
          />
        </div>

        {selectedFile && (
          <div style={fileInfoStyle}>
            <div style={fileNameStyle}>{selectedFile.name}</div>
            <div style={fileSizeStyle}>{formatFileSize(selectedFile.size)}</div>
          </div>
        )}

        <div style={buttonContainerStyle}>
          <button
            style={cancelButtonStyle}
            onClick={handleCancel}
            disabled={isUploading}
            onMouseEnter={(e) => {
              if (!isUploading) {
                e.target.style.backgroundColor = colors.background.secondary
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white'
            }}
          >
            Cancel
          </button>
          <button
            style={uploadButtonStyle}
            onClick={handleUpload}
            disabled={isUploading || !selectedFile}
            onMouseEnter={(e) => {
              if (!isUploading && selectedFile) {
                e.target.style.backgroundColor = colors.primaryDark
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.primary
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UploadMediaModal

